import { notification } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck } from "react-icons/bi";
import {
  MdAdd,
  MdClose,
  MdDeleteOutline,
  MdErrorOutline,
  MdOutlineBookmark,
  MdOutlineCheckCircle,
  MdOutlineCloudUpload,
  MdOutlineMap,
  MdOutlineWarning,
} from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ADD_REFERENTIAL,
  IMPORT_REFERENTIAL_FROM_FILE,
} from "utils/api/graphql/mutations/geo-zones";
import { REFERENTIALS } from "utils/api/graphql/queries/geo-zones";
import { DEPARTMENTS } from "utils/constants";
import { COLORS } from "shared/style/colors";
import {
  CREATE_MODES,
  FILE_ACCEPT_STRING,
  REFERENTIAL_QUERY_LIMIT,
  REFERENTIAL_TYPES,
} from "./constants";
import StepCard from "./StepCard";

const StepGeoZone = ({
  fileZones,
  onConfirm,
  persistedState,
  onStateChange,
  onBack,
  backLabel,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const IMPORT = t("IMPORT", { returnObjects: true });
  const GZ = IMPORT.GEO_ZONE;

  const { zoneDeptMap, saveAsRef, refName, selectedRefId } = persistedState;

  const updateField = (field, value) => {
    onStateChange((prev) => ({ ...prev, [field]: value }));
  };

  const setZoneDeptMap = (val) =>
    onStateChange((prev) => ({
      ...prev,
      zoneDeptMap: typeof val === "function" ? val(prev.zoneDeptMap) : val,
    }));
  const setSaveAsRef = (val) => updateField("saveAsRef", val);
  const setRefName = (val) => updateField("refName", val);
  const setSelectedRefId = (val) => updateField("selectedRefId", val);

  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState(CREATE_MODES.UPLOAD);
  const [newName, setNewName] = useState("");
  const [manualEntries, setManualEntries] = useState([
    { zoneCode: "", department: "" },
  ]);
  const fileInputRef = useRef(null);

  const { data: refsData, refetch } = useQuery(REFERENTIALS, {
    variables: {
      where: { type: REFERENTIAL_TYPES.GEO_ZONE },
      take: REFERENTIAL_QUERY_LIMIT,
    },
  });

  const existingRefs = get(refsData, "referentials.data", []);

  const [addReferential, { loading: addingManual }] =
    useMutation(ADD_REFERENTIAL);
  const [importReferential, { loading: importing }] = useMutation(
    IMPORT_REFERENTIAL_FROM_FILE,
  );

  const departmentOptions = DEPARTMENTS.map((value) => ({
    label: value,
    value,
  }));

  const zonesMapped = fileZones.filter(
    (z) => zoneDeptMap[z] && zoneDeptMap[z].length > 0,
  ).length;
  const allZonesMapped = zonesMapped === fileZones.length;
  const canSave = !saveAsRef || refName.trim();

  const zoneOverlaps = useMemo(() => {
    const deptToZones = {};
    for (const zone of fileZones) {
      for (const dept of zoneDeptMap[zone] || []) {
        if (!deptToZones[dept]) deptToZones[dept] = [];
        deptToZones[dept].push(zone);
      }
    }
    return Object.entries(deptToZones)
      .filter(([, zones]) => zones.length > 1)
      .map(([dept, zones]) => ({ dept, zones }));
  }, [zoneDeptMap, fileZones]);

  const hasOverlaps = zoneOverlaps.length > 0;

  const handleLoadRef = (refId) => {
    if (!refId) {
      setSelectedRefId(null);
      return;
    }
    const ref = existingRefs.find((r) => r.id === refId);
    if (!ref || !ref.entries) return;

    const loaded = {};
    for (const [zoneCode, departments] of Object.entries(ref.entries)) {
      loaded[zoneCode] = departments;
    }
    setZoneDeptMap((prev) => ({ ...prev, ...loaded }));
    setRefName(ref.name);
    setSelectedRefId(ref.id);
    setSaveAsRef(true);
  };

  const handleCreateManual = async () => {
    const validEntries = manualEntries.filter(
      (e) => e.zoneCode && e.department,
    );
    if (!newName || validEntries.length === 0) return;

    const entries = {};
    for (const entry of validEntries) {
      if (!entries[entry.zoneCode]) entries[entry.zoneCode] = [];
      if (!entries[entry.zoneCode].includes(entry.department)) {
        entries[entry.zoneCode].push(entry.department);
      }
    }

    try {
      const { data } = await addReferential({
        variables: {
          data: {
            name: newName,
            type: REFERENTIAL_TYPES.GEO_ZONE,
            entries,
          },
        },
      });
      const created = get(data, "addReferential");
      setShowCreate(false);
      refetch();
      if (created && created.entries) {
        setZoneDeptMap((prev) => ({ ...prev, ...created.entries }));
        setRefName(created.name);
        setSelectedRefId(created.id);
      }
    } catch (err) {
      notification.error({
        message: GZ.CREATE_ERROR,
        description: err.message,
      });
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !newName) return;

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await importReferential({
          refetchQueries: [
            {
              query: REFERENTIALS,
              variables: {
                where: { type: REFERENTIAL_TYPES.GEO_ZONE },
                take: REFERENTIAL_QUERY_LIMIT,
              },
            },
          ],
          variables: {
            data: {
              name: newName,
              type: REFERENTIAL_TYPES.GEO_ZONE,
              base64: reader.result,
              filename: file.name,
            },
          },
        });
        const created = get(data, "importReferentialFromFile");
        setShowCreate(false);
        refetch();
        if (created && created.entries) {
          setZoneDeptMap((prev) => ({ ...prev, ...created.entries }));
          setRefName(created.name);
          setSelectedRefId(created.id);
        }
      } catch (err) {
        notification.error({
          message: GZ.CREATE_ERROR,
          description: err.message,
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const addEntry = () => {
    setManualEntries([...manualEntries, { zoneCode: "", department: "" }]);
  };

  const removeEntry = (idx) => {
    setManualEntries(manualEntries.filter((_, i) => i !== idx));
  };

  const handleCancelCreate = () => {
    setShowCreate(false);
    setNewName("");
    setCreateMode(CREATE_MODES.UPLOAD);
    setManualEntries([{ zoneCode: "", department: "" }]);
  };

  const updateEntry = (idx, field, value) => {
    const updated = [...manualEntries];
    updated[idx] = { ...updated[idx], [field]: value };
    setManualEntries(updated);
  };

  const handleConfirm = async () => {
    if (saveAsRef && refName.trim()) {
      setSaving(true);
      try {
        const refData = {
          name: refName.trim(),
          type: REFERENTIAL_TYPES.GEO_ZONE,
          entries: zoneDeptMap,
        };
        if (selectedRefId) {
          refData.id = selectedRefId;
        }
        await addReferential({
          refetchQueries: [
            {
              query: REFERENTIALS,
              variables: {
                where: { type: REFERENTIAL_TYPES.GEO_ZONE },
                take: REFERENTIAL_QUERY_LIMIT,
              },
            },
          ],
          variables: { data: refData },
        });
        notification.success({ message: GZ.REFERENTIAL_SAVED });
      } catch (error) {
        notification.error({
          message: GZ.REFERENTIAL_SAVE_ERROR,
          description: error.message,
        });
      } finally {
        setSaving(false);
      }
    }

    onConfirm(zoneDeptMap);
  };

  const confirmButton = (
    <Button
      type="primary"
      onClick={handleConfirm}
      disabled={!allZonesMapped || !canSave || saving || hasOverlaps}
      loading={saving}
    >
      <BiCheck size={20} />
      {GZ.LAUNCH_IMPORT}
    </Button>
  );

  return (
    <StepCard
      title={GZ.TITLE}
      description={GZ.DESCRIPTION}
      onBack={onBack}
      backLabel={backLabel}
      actions={confirmButton}
    >
      {existingRefs.length > 0 && (
        <div className="pricers-import__ref-loader">
          <MdOutlineMap size={20} color={COLORS.C_PRIMARY} />
          <span className="pricers-import__ref-loader-label">
            {GZ.EXISTING}
          </span>
          <Select
            options={existingRefs.map((r) => ({
              label: `${r.name} (${Object.keys(r.entries || {}).length} zones)`,
              value: r.id,
            }))}
            placeholder={GZ.SELECT_REFERENTIAL}
            onChange={handleLoadRef}
            allowClear
            className="w-300"
          />
        </div>
      )}

      {!showCreate && (
        <Button
          type="primary--outlined"
          onClick={() => setShowCreate(true)}
          className="mb-24"
        >
          <MdAdd size={20} />
          {GZ.CREATE_NEW}
        </Button>
      )}

      {showCreate && (
        <div className="pricers-import__create-form">
          <Input
            label={GZ.NAME_LABEL}
            placeholder={GZ.NAME_PLACEHOLDER}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />

          <div className="grid--2 gap-12 mt-16 mb-16">
            <div
              onClick={() => setCreateMode(CREATE_MODES.UPLOAD)}
              className={classNames("pricers-import__mode-card", {
                "pricers-import__mode-card--active":
                  createMode === CREATE_MODES.UPLOAD,
              })}
            >
              <MdOutlineCloudUpload size={24} color={COLORS.C_PRIMARY} />
              <div className="pricers-import__mode-label">{GZ.UPLOAD_FILE}</div>
            </div>
            <div
              onClick={() => setCreateMode(CREATE_MODES.MANUAL)}
              className={classNames("pricers-import__mode-card", {
                "pricers-import__mode-card--active":
                  createMode === CREATE_MODES.MANUAL,
              })}
            >
              <MdAdd size={24} color={COLORS.C_PRIMARY} />
              <div className="pricers-import__mode-label">
                {GZ.MANUAL_ENTRY}
              </div>
            </div>
          </div>

          {createMode === CREATE_MODES.UPLOAD && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={FILE_ACCEPT_STRING}
                className="hidden-input"
                onChange={handleFileUpload}
              />
              <Button
                type="primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={!newName || importing}
                loading={importing}
              >
                <MdOutlineCloudUpload size={20} />
                {GZ.SELECT_FILE}
              </Button>
              <p className="pricers-import__file-hint">{GZ.FILE_HINT}</p>
            </div>
          )}

          {createMode === CREATE_MODES.MANUAL && (
            <div>
              {manualEntries.map((entry, idx) => (
                <div key={idx} className="pricers-import__entry-row">
                  <Input
                    placeholder={GZ.ZONE_LABEL}
                    value={entry.zoneCode}
                    onChange={(e) =>
                      updateEntry(idx, "zoneCode", e.target.value)
                    }
                    className="flex--grow-1"
                  />
                  <Input
                    placeholder={GZ.DEPARTMENT}
                    value={entry.department}
                    onChange={(e) =>
                      updateEntry(idx, "department", e.target.value)
                    }
                    className="flex--grow-1"
                  />
                  <MdDeleteOutline
                    size={20}
                    color={COLORS.C_DANGER}
                    className="pricers-import__clickable"
                    onClick={() => removeEntry(idx)}
                  />
                </div>
              ))}
              <div className="d-flex gap-8 mt-12">
                <Button type="primary--outlined" onClick={addEntry}>
                  <MdAdd size={18} />
                  {GZ.ADD_ROW}
                </Button>
                <Button
                  type="primary"
                  onClick={handleCreateManual}
                  disabled={!newName || addingManual}
                  loading={addingManual}
                >
                  {GZ.CREATE_BUTTON}
                </Button>
              </div>
            </div>
          )}

          <Button
            type="danger--outlined"
            onClick={handleCancelCreate}
            className="mt-16"
          >
            <MdClose size={18} />
            {GZ.CANCEL_CREATE}
          </Button>
        </div>
      )}

      {fileZones.length > 0 && (
        <div className="pricers-import__vm-section">
          <div className="pricers-import__vm-section-header">
            <h4 className="m-0">{GZ.ZONES_MAPPING_TITLE}</h4>
            <div
              className={classNames("pricers-import__mapping-status", {
                "pricers-import__mapping-status--done": allZonesMapped,
                "pricers-import__mapping-status--pending": !allZonesMapped,
              })}
            >
              {allZonesMapped ? (
                <MdOutlineCheckCircle size={16} />
              ) : (
                <MdOutlineWarning size={16} />
              )}
              {allZonesMapped
                ? GZ.ALL_ZONES_MAPPED
                : `${fileZones.length - zonesMapped} ${GZ.ZONES_REMAINING}`}
            </div>
          </div>

          <div className="pricers-import__vm-rows">
            {fileZones.map((zone) => {
              const hasDepts =
                zoneDeptMap[zone] && zoneDeptMap[zone].length > 0;
              const usedByOthers = new Set(
                fileZones
                  .filter((z) => z !== zone)
                  .flatMap((z) => zoneDeptMap[z] || []),
              );
              const zoneOptions = departmentOptions.filter(
                (opt) => !usedByOthers.has(opt.value),
              );
              return (
                <div
                  key={zone}
                  className={classNames("pricers-import__vm-row", {
                    "pricers-import__vm-row--mapped": hasDepts,
                  })}
                >
                  <div className="pricers-import__vm-file-value">
                    <span
                      className={classNames("pricers-import__status-dot", {
                        "pricers-import__status-dot--mapped": hasDepts,
                        "pricers-import__status-dot--required": !hasDepts,
                      })}
                    />
                    <span className="pricers-import__file-value">{zone}</span>
                  </div>
                  <div className="pricers-import__vm-arrow">→</div>
                  <Select
                    mode="multiple"
                    options={zoneOptions}
                    value={zoneDeptMap[zone] || []}
                    placeholder={GZ.SELECT_DEPARTMENTS}
                    onChange={(val) =>
                      setZoneDeptMap((prev) => ({
                        ...prev,
                        [zone]: val,
                      }))
                    }
                    className="pricers-import__vm-select"
                  />
                </div>
              );
            })}
          </div>

          {hasOverlaps && (
            <div className="pricers-import__overlap-errors">
              {zoneOverlaps.map(({ dept, zones }) => {
                const message = GZ.OVERLAP_ERROR.replace(
                  "{{dept}}",
                  dept,
                ).replace("{{zones}}", zones.join(", "));
                return (
                  <div key={dept} className="pricers-import__overlap-error">
                    <MdErrorOutline size={18} color={COLORS.C_DANGER} />
                    <span>{message}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="pricers-import__save-section mb-24">
        <div
          className={classNames("d-flex align--center gap-10", {
            "mb-12": saveAsRef,
          })}
        >
          <input
            type="checkbox"
            checked={saveAsRef}
            onChange={(e) => setSaveAsRef(e.target.checked)}
            className="pricers-checkbox pricers-checkbox--lg"
          />
          <MdOutlineBookmark size={18} color={COLORS.C_PRIMARY} />
          <span className="pricers-import__save-label">
            {GZ.SAVE_AS_REFERENTIAL}
          </span>
        </div>
        {saveAsRef && (
          <Input
            placeholder={GZ.REFERENTIAL_NAME_PLACEHOLDER}
            value={refName}
            onChange={(e) => setRefName(e.target.value)}
          />
        )}
      </div>
    </StepCard>
  );
};

export default StepGeoZone;
